import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import type { UIMessage } from "ai";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps, HTMLAttributes } from "react";
import Image from "next/image";

export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage["role"];
};

export const Message = ({ className, from, ...props }: MessageProps) => (
  <div
    className={cn(
      "group flex w-full items-start gap-2 py-3 sm:gap-3 sm:py-4",
      from === "user" ? "is-user justify-end" : "is-assistant justify-start",
      className
    )}
    {...props}
  />
);

const messageContentVariants = cva(
  "is-user:dark flex flex-col gap-2 overflow-hidden text-sm leading-relaxed transition-all duration-200",
  {
    variants: {
      variant: {
        contained: [
          "max-w-[90%] px-4 py-3 sm:max-w-[80%] sm:px-5 sm:py-3.5",
          // User messages: MindUP pink-to-plum gradient
          "group-[.is-user]:bg-gradient-to-br group-[.is-user]:from-[var(--mindup-pink)] group-[.is-user]:to-[var(--mindup-plum)] group-[.is-user]:text-white group-[.is-user]:shadow-lg group-[.is-user]:shadow-[var(--mindup-pink)]/25 group-[.is-user]:rounded-2xl group-[.is-user]:rounded-br-md",
          // Assistant messages: clean white/off-white
          "group-[.is-assistant]:bg-gradient-to-br group-[.is-assistant]:from-white group-[.is-assistant]:to-[var(--mindup-off-white)] group-[.is-assistant]:dark:from-[#363636] group-[.is-assistant]:dark:to-[#3a3a3a] group-[.is-assistant]:text-[var(--mindup-soft-black)] group-[.is-assistant]:dark:text-white group-[.is-assistant]:shadow-md group-[.is-assistant]:shadow-gray-200/50 group-[.is-assistant]:dark:shadow-black/20 group-[.is-assistant]:border group-[.is-assistant]:border-gray-100 group-[.is-assistant]:dark:border-white/10 group-[.is-assistant]:rounded-2xl group-[.is-assistant]:rounded-bl-md",
        ],
        flat: [
          "group-[.is-user]:max-w-[90%] group-[.is-user]:bg-gradient-to-br group-[.is-user]:from-[var(--mindup-pink-light)] group-[.is-user]:to-[var(--mindup-pink-light)]/60 group-[.is-user]:dark:from-[#3a3a3a] group-[.is-user]:dark:to-[#363636] group-[.is-user]:px-4 group-[.is-user]:py-3 group-[.is-user]:text-[var(--mindup-soft-black)] group-[.is-user]:dark:text-white group-[.is-user]:shadow-sm group-[.is-user]:border group-[.is-user]:border-[var(--mindup-pink-light)]/50 group-[.is-user]:dark:border-white/10 group-[.is-user]:rounded-2xl group-[.is-user]:rounded-br-md sm:group-[.is-user]:max-w-[80%] sm:group-[.is-user]:px-5 sm:group-[.is-user]:py-3.5",
          "group-[.is-assistant]:text-[var(--mindup-soft-black)] group-[.is-assistant]:dark:text-white",
        ],
      },
    },
    defaultVariants: {
      variant: "contained",
    },
  }
);

export type MessageContentProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof messageContentVariants>;

export const MessageContent = ({
  children,
  className,
  variant,
  ...props
}: MessageContentProps) => (
  <div
    className={cn(messageContentVariants({ variant, className }))}
    {...props}
  >
    {children}
  </div>
);

export type MessageAvatarProps = ComponentProps<typeof Avatar> & {
  src: string;
  name?: string;
};

export const MessageAvatar = ({
  src,
  name,
  className,
  ...props
}: MessageAvatarProps) => (
  <Avatar className={cn("size-8 ring-1 ring-border", className)} {...props}>
    <AvatarImage alt="" className="mt-0 mb-0" src={src} />
    <AvatarFallback>{name?.slice(0, 2) || "ME"}</AvatarFallback>
  </Avatar>
);

// Assistant avatar — uses the official MindUP brain avatar
export const AssistantAvatar = ({ className }: { className?: string }) => (
  <div className={cn(
    "flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full overflow-hidden shadow-md shadow-[var(--mindup-pink)]/20",
    className
  )}>
    <Image
      src="/brand/avatars/MindUP_Avatars_RGB_BrainAvatar.png"
      alt="MindUP"
      width={32}
      height={32}
      className="w-full h-full object-cover"
    />
  </div>
);

// User avatar — brand blue family
export const UserAvatar = ({ className }: { className?: string }) => (
  <div className={cn(
    "flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-[var(--mindup-blue)] to-[var(--mindup-navy)] flex items-center justify-center shadow-md shadow-[var(--mindup-blue)]/20",
    className
  )}>
    <span className="text-[10px] sm:text-xs font-bold text-white tracking-tight">You</span>
  </div>
);
